import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Table,
  Tooltip,
  OverlayTrigger,
  Button as ReactButton,
} from 'react-bootstrap';
import SimpleHeader from 'components/shared/table/SimpleHeader';
import SimplePagination from 'components/shared/pagination/SimplePagination';
import './SubmittalLogTable.css';
import Date from 'components/shared/text/Date';
import Button from 'components/shared/button/Button';
import BaseStatus from 'components/shared/text/BaseStatus';
import {
  SubmittalStatusEnum,
  submittalStatusLabel,
} from 'domain/submittal-status-enum';
import { TYPES, usersUtil } from 'services/utils/users-util';
import { Enterprise } from 'domain/enterprise';
import { Submittal } from 'domain/submittal';

class SubmittalLogTable extends Component {
  static propTypes = {
    submittals: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    total: PropTypes.number.isRequired,
    currentPage: PropTypes.number.isRequired,
    currentStatus: PropTypes.string.isRequired,
    pageSize: PropTypes.number.isRequired,
    currentUser: PropTypes.shape({}).isRequired,
    contribType: PropTypes.string.isRequired,
    unreadById: PropTypes.shape({}),
    submittalTabSelected: PropTypes.number.isRequired,
    handlePageChange: PropTypes.func.isRequired,
    handleOpenSubmittal: PropTypes.func.isRequired,
  };

  subcontractorExists = (submittal) => {
    const { currentUser } = this.props;
    return (
      currentUser.accountable_id === submittal.contractor_id &&
      usersUtil.hasType(currentUser, TYPES.CONTRACTOR)
    );
  };

  assigneeExists = (submittal) => {
    const { currentUser } = this.props;
    return Submittal.assignee(submittal, currentUser);
  };

  addlAssignee = (submittal) => {
    const { currentUser } = this.props;
    return Submittal.addlAssignee(submittal, currentUser);
  };

  responderExists = (submittal) => {
    const { currentUser } = this.props;
    const addlAssignee = this.addlAssignee(submittal);
    return (
      submittal.responded_by_id === currentUser.id ||
      !!(addlAssignee && addlAssignee.stamp_action)
    );
  };

  dueDate = (submittal) => {
    const addlAssignee = this.addlAssignee(submittal);
    return addlAssignee
      ? addlAssignee.due_date
      : submittal.extended_due_date
      ? submittal.extended_due_date
      : submittal.due_date;
  };

  returnedAt = (submittal) => {
    const addlAssignee = this.addlAssignee(submittal);
    return addlAssignee ? addlAssignee.returned_at : submittal.returned_at;
  };

  renderAssigneeTooltip = (label, status) => {
    return (
      <Tooltip id="tooltip-assignee-toggle">
        {label}
        {status && <div>{status}</div>}
      </Tooltip>
    );
  };

  renderAssigneeCircle = (label, status, checked, key) => {
    return (
      <OverlayTrigger
        key={key}
        placement="bottom"
        overlay={this.renderAssigneeTooltip(label, status)}
      >
        <ReactButton className="addl-assignee-circle">
          <span role="img" aria-label="circle">
            {checked ? '🟢' : '⚪'}
          </span>
        </ReactButton>
      </OverlayTrigger>
    );
  };

  handleGenerateLink = (id) => {
    return `?submittalId=${id}`;
  };

  mapSubmittals = () => {
    const { unreadById, handleOpenSubmittal } = this.props;
    let currentCsiCodeId = null;
    return [].concat(
      ...this.props.submittals.map((submittal, i) => {
        const sub = this.subcontractorExists(submittal);
        const assigned = this.assigneeExists(submittal);
        const responded = this.responderExists(submittal);
        const link = this.handleGenerateLink(submittal.id);
        let row = [];
        if (currentCsiCodeId !== submittal.csi_code.id) {
          currentCsiCodeId = submittal.csi_code.id;
          row.push(
            <tr key={`csi-${currentCsiCodeId}_${i}`} className="csi-code-row">
              <td>{submittal.csi_code.division}</td>
              <td className="csi-code-label">{submittal.csi_code.label}</td>
            </tr>
          );
        }

        const dueDate = this.dueDate(submittal);
        const returnedAt = this.returnedAt(submittal);
        const submittalStatusEnum = SubmittalStatusEnum.create(
          submittal.status
        );
        const draftSubmittal =
          SubmittalStatusEnum.DRAFT.equals(submittalStatusEnum) ||
          SubmittalStatusEnum.DRAFT_PENDING.equals(submittalStatusEnum) ||
          SubmittalStatusEnum.DRAFT_ASSIGNED.equals(submittalStatusEnum);
        const unread = unreadById && !!unreadById[submittal.id];
        row.push(
          <tr
            key={submittal.id}
            className="selectable link-tab"
            onClick={(e) => {
              e.preventDefault();
              handleOpenSubmittal(submittal.id);
            }}
          >
            <td>
              <a href={link}>
                {unread && (
                  <BaseStatus
                    labels={[
                      {
                        id: 'unread',
                        label: '',
                      },
                    ]}
                    status="unread"
                  />
                )}
                {submittal.csi_code.division}-{submittal.number}
              </a>
            </td>
            <td className="submittal-title">
              <a href={link}>{submittal.title || '-'}</a>
            </td>
            <td>
              <a href={link}>
                {submittal.requested_at ? (
                  <Date value={submittal.requested_at} format="MM/DD/YYYY" />
                ) : (
                  '-'
                )}
              </a>
            </td>
            <td>
              <a href={link}>
                {submittal.received_at ? (
                  <Date value={submittal.received_at} format="MM/DD/YYYY" />
                ) : (
                  '-'
                )}
              </a>
            </td>
            <td>
              <a href={link}>
                {submittal.submitted_at ? (
                  <Date value={submittal.submitted_at} format="MM/DD/YYYY" />
                ) : (
                  '-'
                )}
              </a>
            </td>
            <td>
              <a href={link}>
                {dueDate ? <Date value={dueDate} format="MM/DD/YYYY" /> : '-'}
              </a>
            </td>
            <td>
              <a href={link}>
                {returnedAt ? (
                  <Date value={returnedAt} format="MM/DD/YYYY" />
                ) : (
                  '-'
                )}
              </a>
            </td>
            <td>
              <a href={link}>
                {submittal.distributed_at ? (
                  <Date value={submittal.distributed_at} format="MM/DD/YYYY" />
                ) : (
                  '-'
                )}
              </a>
            </td>
            <td>
              <a href={link}>
                <div
                  className={
                    draftSubmittal
                      ? 'submittal-status draft'
                      : 'submittal-status'
                  }
                >
                  {submittal.assignee_label ? (
                    <OverlayTrigger
                      placement="bottom"
                      overlay={this.renderAssigneeTooltip(
                        submittal.assignee_label
                      )}
                    >
                      <ReactButton className="addl-assignee-circle">
                        {submittalStatusLabel(submittal)}
                      </ReactButton>
                    </OverlayTrigger>
                  ) : (
                    submittalStatusLabel(submittal)
                  )}
                </div>
                {submittal.submittal_addl_assignees.length > 0 &&
                  submittal.stamp_action &&
                  this.renderAssigneeCircle(
                    submittal.assignee_label,
                    submittal.stamp_title,
                    true
                  )}
                {submittal.submittal_addl_assignees
                  .filter((addlAssignee) => !!addlAssignee.stamp_action)
                  .map((addlAssignee, index) => {
                    return this.renderAssigneeCircle(
                      addlAssignee.assignee_label,
                      addlAssignee.stamp_title,
                      true,
                      index
                    );
                  })}
                {submittal.submittal_addl_assignees.length > 0 &&
                  !submittal.stamp_action &&
                  this.renderAssigneeCircle(
                    submittal.assignee_label,
                    null,
                    false
                  )}
                {submittal.submittal_addl_assignees
                  .filter((addlAssignee) => !addlAssignee.stamp_action)
                  .map((addlAssignee, index) => {
                    return this.renderAssigneeCircle(
                      addlAssignee.assignee_label,
                      null,
                      false,
                      index
                    );
                  })}
              </a>
            </td>
            <td>
              <a href={link}>
                {assigned && !submittal.closed && !responded ? (
                  <Button className="button-blue-grey" label="Respond" />
                ) : (
                  <Button
                    className={
                      sub || (assigned && !submittal.closed)
                        ? 'button-blue-grey'
                        : 'button-white'
                    }
                    label="View"
                  />
                )}
              </a>
            </td>
          </tr>
        );

        return row;
      })
    );
  };

  render() {
    const {
      submittals,
      contribType,
      currentPage,
      currentStatus,
      pageSize,
      total,
      submittalTabSelected,
      handlePageChange,
      dueDateFilter = 'all',
    } = this.props;
    const headers = [
      'CSI CODE',
      'TITLE',
      'REQUESTED',
      'RECEIVED',
      'SUBMITTED',
      <SimpleHeader
        label="DUE DATE"
        filter={{
          options: [
            {
              id: 'all',
              label: 'All',
            },
            {
              id: 'pending',
              label: 'Pending',
            },
            {
              id: 'overdue',
              label: 'Overdue',
            },
            {
              id: 'ascending',
              label: 'Ascending',
            },
            {
              id: 'descending',
              label: 'Descending',
            },
          ].filter((st) => !!st),
          selected: dueDateFilter,
        }}
        onFilterOptionSelect={(filter) => {
          handlePageChange(1, currentStatus, filter);
        }}
      />,
      'RETURNED',
      'DISTRIBUTED',
      submittalTabSelected === 1 ? (
        'STATUS'
      ) : (
        <SimpleHeader
          label="STATUS"
          filter={{
            options: [
              Enterprise.spackleOrGc(contribType)
                ? {
                    id: 'not_pre',
                    label: 'All',
                  }
                : {
                    id: 'all',
                    label: 'All',
                  },
              (Enterprise.spackleOrGc(contribType) ||
                Enterprise.subcontractor(contribType)) && {
                id: 'draft',
                label: 'Draft',
              },
              Enterprise.subcontractor(contribType) && {
                id: 'pre',
                label: 'Pending',
              },
              {
                id: 'assigned_or_draft_assigned',
                label: 'Assigned',
              },
              {
                id: 'cls',
                label: 'Closed',
              },
            ].filter((st) => !!st),
            selected: currentStatus,
          }}
          onFilterOptionSelect={(status) => {
            handlePageChange(1, status, dueDateFilter);
          }}
        />
      ),
      '',
    ];
    return (
      <div className="submittal-log-table-container">
        <Table className="simple-table projects-table submittal-log-table">
          <colgroup>
            {headers.map((element, index) => {
              return <col key={index + 1} />;
            })}
          </colgroup>

          <thead>
            <tr>
              {headers.map((element, index) => {
                return (
                  <th key={index}>
                    <span>{element}</span>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {submittals.length === 0 ? (
              <tr>
                <td className="td-empty-table" colSpan={headers.length}>
                  No Submittals added yet.
                </td>
              </tr>
            ) : (
              this.mapSubmittals()
            )}
          </tbody>
        </Table>
        <div className="pagination-container">
          <SimplePagination
            currentPage={currentPage}
            pageSize={pageSize}
            total={total}
            onPageChange={(page) => {
              handlePageChange(page, currentStatus);
            }}
          />
        </div>
      </div>
    );
  }
}

export default connect()(SubmittalLogTable);



// WEBPACK FOOTER //
// ./src/components/admin/projects/details/project-submittal-log/SubmittalLogTable.js